// OnboardingWizard — blocking step-by-step modal shown to role=user accounts
// who are missing any of: phone, shipping address, billing address.
//
// 3 sequential steps:
//   1. Phone number (numeric, basic India format check)
//   2. Shipping address — Google Places autocomplete + map pin
//   3. Billing address — same as shipping + "same as shipping" checkbox
//
// Trigger: every login, re-evaluated whenever /api/auth/me data changes. As
// soon as all 3 are present the wizard self-dismisses for the rest of the
// session. No skip option — user CANNOT close the dialog without finishing.
import { useEffect, useRef, useState } from "react";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Phone as PhoneIcon, MapPin, FileText, Loader2, Search, Check, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { GoogleMap, useJsApiLoader, Marker as GMarker, Autocomplete } from "@react-google-maps/api";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const GOOGLE_LIBRARIES = ["places"];
const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };
const INDIA_BOUNDS = { north: 35.5, south: 6.5, east: 97.5, west: 68.0 };

// Strip non-digits then validate as 10-digit Indian mobile (defensive but
// permissive — allows leading 0 or country code which we trim).
function normalisePhone(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  return digits;
}

function emptyAddress() {
  return {
    label: "Home",
    address_line: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    lat: INDIA_CENTER.lat,
    lng: INDIA_CENTER.lng,
  };
}

export default function OnboardingWizard() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [phone, setPhone] = useState("");
  const [shippingAddr, setShippingAddr] = useState(emptyAddress());
  const [billingAddr, setBillingAddr] = useState(emptyAddress());
  const [copyShippingToBilling, setCopyShippingToBilling] = useState(true);
  const [hasShipping, setHasShipping] = useState(false);
  const [hasBilling, setHasBilling] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY, libraries: GOOGLE_LIBRARIES });

  // Decide whether to show the wizard. Only role=user, only when missing data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { if (!cancelled) setOpen(false); return; }
      if (user.role !== "user") { if (!cancelled) setOpen(false); return; }
      try {
        // Fetch shipping addresses + billing address (separate singleton collection) in parallel
        const [ar, br] = await Promise.all([
          fetch(`${API}/addresses`, { credentials: "include" }),
          fetch(`${API}/billing-address`, { credentials: "include" }),
        ]);
        const addrs = ar.ok ? await ar.json() : [];
        const billing = br.ok ? await br.json() : {};
        if (cancelled) return;
        const hasShip = addrs.some((a) => a.address_line);
        const hasBill = !!(billing && billing.address_line);
        const hasPh = !!(user.phone && String(user.phone).trim());
        setHasShipping(hasShip);
        setHasBilling(hasBill);
        setHasPhone(hasPh);
        // If user already has a shipping address but no billing, hydrate
        // shippingAddr state from the default/first existing record so the
        // "Same as shipping" copy-to-billing checkbox has something to work
        // with on step 3.
        if (hasShip && !hasBill) {
          const ship = addrs.find((a) => a.is_default) || addrs[0];
          if (ship) {
            setShippingAddr({
              label: ship.label || "Home",
              address_line: ship.address_line || "",
              landmark: ship.landmark || "",
              city: ship.city || "",
              state: ship.state || "",
              pincode: ship.pincode || "",
              lat: ship.lat ?? INDIA_CENTER.lat,
              lng: ship.lng ?? INDIA_CENTER.lng,
            });
          }
        }
        const needsAnything = !hasPh || !hasShip || !hasBill;
        if (needsAnything) {
          setPhone(user.phone || "");
          // Start at the first missing step
          if (!hasPh) setStep(1);
          else if (!hasShip) setStep(2);
          else setStep(3);
          setOpen(true);
        } else {
          setOpen(false);
        }
      } catch (e) {
        console.error("Onboarding status check failed:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!open) return null;

  const goNext = () => {
    if (step === 1) {
      // Phone validation
      const norm = normalisePhone(phone);
      if (!/^\d{10}$/.test(norm)) {
        toast.error("Please enter a valid 10-digit Indian mobile number");
        return;
      }
      setPhone(norm);
      if (!hasShipping) setStep(2);
      else if (!hasBilling) setStep(3);
      else handleFinish();
    } else if (step === 2) {
      if (!shippingAddr.address_line.trim()) { toast.error("Please pick a shipping address"); return; }
      if (!shippingAddr.city.trim() || !shippingAddr.state.trim()) { toast.error("City and State are required"); return; }
      if (copyShippingToBilling) {
        setBillingAddr({ ...shippingAddr, label: "Billing" });
      }
      setStep(3);
    } else if (step === 3) {
      const src = copyShippingToBilling ? { ...shippingAddr, label: "Billing" } : billingAddr;
      if (!src.address_line.trim() || !src.city.trim() || !src.state.trim()) {
        toast.error("Please fill in the billing address (or check 'Same as shipping')");
        return;
      }
      handleFinish(src);
    }
  };

  const goBack = () => {
    if (step === 3) {
      if (!hasShipping) setStep(2);
      else if (!hasPhone) setStep(1);
    } else if (step === 2) {
      if (!hasPhone) setStep(1);
    }
  };

  const handleFinish = async (billingOverride) => {
    setSubmitting(true);
    try {
      // 1. Save phone if missing
      if (!hasPhone) {
        const r = await fetch(`${API}/profile`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ phone }),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || "Failed to save phone");
        }
      }
      // 2. Save shipping if missing
      if (!hasShipping) {
        const r = await fetch(`${API}/addresses`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ...shippingAddr, is_default: true }),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || "Failed to save shipping address");
        }
      }
      // 3. Save billing if missing — uses the SEPARATE singleton
      //    /billing-address endpoint (upsert), NOT the shipping addresses
      //    collection. This keeps the billing entry from polluting the
      //    delivery address list on the profile page.
      if (!hasBilling) {
        const finalBilling = billingOverride || (copyShippingToBilling ? { ...shippingAddr, label: "Billing" } : billingAddr);
        const r = await fetch(`${API}/billing-address`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            address_line: finalBilling.address_line,
            landmark: finalBilling.landmark,
            city: finalBilling.city,
            state: finalBilling.state,
            pincode: finalBilling.pincode,
          }),
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || "Failed to save billing address");
        }
      }
      await refreshUser();
      toast.success("Profile completed! You're all set.");
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Could not save your info");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSteps = (hasPhone ? 0 : 1) + (hasShipping ? 0 : 1) + (hasBilling ? 0 : 1);
  const currentIndex = ((!hasPhone && step >= 1) ? 1 : 0) + ((!hasShipping && step >= 2) ? 1 : 0) + ((!hasBilling && step >= 3) ? 1 : 0);

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-3 sm:p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-5 py-4 bg-emerald-700 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              {step === 1 && <PhoneIcon size={18} />}
              {step === 2 && <MapPin size={18} />}
              {step === 3 && <FileText size={18} />}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-100">
                Step {currentIndex} of {totalSteps}
              </p>
              <h2 className="text-base font-extrabold leading-tight">
                {step === 1 && "Add your phone number"}
                {step === 2 && "Add your shipping address"}
                {step === 3 && "Add your billing address"}
              </h2>
            </div>
          </div>
          <div className="flex gap-1 mt-3">
            {[1, 2, 3].map((s) => {
              const skip = (s === 1 && hasPhone) || (s === 2 && hasShipping) || (s === 3 && hasBilling);
              if (skip) return null;
              return (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${step >= s ? "bg-white" : "bg-white/25"}`}
                />
              );
            })}
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {step === 1 && (
            <>
              <p className="text-sm text-gray-600">We need a phone number to coordinate your deliveries. Used only by our kitchen and your rider.</p>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Mobile number *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-semibold">+91</span>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="10-digit mobile number"
                    className="pl-12 h-11 text-base"
                    data-testid="onboarding-phone-input"
                    maxLength={14}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Indian mobile numbers only. No verification SMS — we just need it for delivery contact.</p>
              </div>
            </>
          )}

          {step === 2 && (
            <AddressSubForm
              isLoaded={isLoaded}
              form={shippingAddr}
              setForm={setShippingAddr}
              testIdPrefix="onboarding-shipping"
            />
          )}

          {step === 3 && (
            <>
              <label className="flex items-center gap-2.5 p-3 rounded-xl border border-emerald-200 bg-emerald-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={copyShippingToBilling}
                  onChange={(e) => setCopyShippingToBilling(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="onboarding-copy-shipping"
                />
                <span className="text-sm font-semibold text-emerald-900">Billing address same as shipping</span>
              </label>
              {copyShippingToBilling ? (
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600 space-y-0.5">
                  <p className="font-bold text-gray-800">{shippingAddr.address_line}</p>
                  {shippingAddr.landmark && <p>{shippingAddr.landmark}</p>}
                  <p>{[shippingAddr.city, shippingAddr.state, shippingAddr.pincode].filter(Boolean).join(", ")}</p>
                </div>
              ) : (
                <AddressSubForm
                  isLoaded={isLoaded}
                  form={billingAddr}
                  setForm={setBillingAddr}
                  testIdPrefix="onboarding-billing"
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-white border-t border-gray-100 flex items-center justify-between gap-2">
          {step > 1 && (currentIndex > 1) ? (
            <Button variant="outline" size="sm" onClick={goBack} disabled={submitting} data-testid="onboarding-back">
              <ChevronLeft size={14} className="mr-1" /> Back
            </Button>
          ) : <span />}
          <Button
            onClick={goNext}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
            data-testid="onboarding-next"
          >
            {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
            {step === 3 ? "Finish" : "Next"}
            {step !== 3 && <ChevronRight size={14} className="ml-1" />}
            {step === 3 && !submitting && <Check size={14} className="ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}


// Reused 2x (shipping + billing). Wraps the same Google Places autocomplete +
// map pin UX as the profile page address editor — copy-pasted from there
// rather than refactoring the original to avoid disturbing a working flow.
function AddressSubForm({ isLoaded, form, setForm, testIdPrefix }) {
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);

  const reverseGeocode = async (lat, lng) => {
    if (!window.google?.maps) return;
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });
      const result = response.results?.[0];
      if (!result) return;
      const components = result.address_components || [];
      let city = "", state = "", pincode = "";
      for (const c of components) {
        if (c.types.includes("locality")) city = c.long_name;
        if (c.types.includes("administrative_area_level_1")) state = c.long_name;
        if (c.types.includes("postal_code")) pincode = c.long_name;
        if (!city && c.types.includes("sublocality_level_1")) city = c.long_name;
      }
      setForm((prev) => ({
        ...prev, lat, lng,
        address_line: result.formatted_address?.split(",").slice(0, 3).join(",").trim() || prev.address_line,
        city: city || prev.city, state: state || prev.state, pincode: pincode || prev.pincode,
      }));
    } catch (e) {
      console.error("Reverse geocode failed:", e);
    }
  };

  const onPlaceSelected = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const components = place.address_components || [];
    let city = "", state = "", pincode = "";
    for (const c of components) {
      if (c.types.includes("locality")) city = c.long_name;
      if (c.types.includes("administrative_area_level_1")) state = c.long_name;
      if (c.types.includes("postal_code")) pincode = c.long_name;
      if (!city && c.types.includes("sublocality_level_1")) city = c.long_name;
    }
    setForm((prev) => ({
      ...prev, lat, lng,
      address_line: place.formatted_address?.split(",").slice(0, 3).join(",").trim() || place.name || prev.address_line,
      city: city || prev.city, state: state || prev.state, pincode: pincode || prev.pincode,
    }));
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(16);
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setForm((prev) => ({ ...prev, lat, lng }));
    reverseGeocode(lat, lng);
  };

  return (
    <div className="space-y-3">
      {/* Google Places Search */}
      {isLoaded && (
        <Autocomplete
          onLoad={(auto) => (autocompleteRef.current = auto)}
          onPlaceChanged={onPlaceSelected}
          options={{ componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] }}
        >
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search for a location in India..." className="pl-9 h-10" data-testid={`${testIdPrefix}-search`} />
          </div>
        </Autocomplete>
      )}

      {/* Map */}
      {isLoaded ? (
        <div className="h-44 rounded-xl overflow-hidden border border-gray-200">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: form.lat, lng: form.lng }}
            zoom={13}
            onClick={handleMapClick}
            onLoad={(map) => { mapRef.current = map; }}
            options={{ restriction: { latLngBounds: INDIA_BOUNDS, strictBounds: true }, mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
          >
            <GMarker position={{ lat: form.lat, lng: form.lng }} />
          </GoogleMap>
        </div>
      ) : (
        <div className="h-44 rounded-xl bg-gray-100 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
      )}
      <p className="text-[10px] text-gray-500 -mt-1">Search or tap on the map to pin your location</p>

      <div className="space-y-2">
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">Address line *</Label>
          <Input value={form.address_line} onChange={(e) => setForm((p) => ({ ...p, address_line: e.target.value }))} placeholder="Flat / building / street" data-testid={`${testIdPrefix}-line`} />
        </div>
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">Landmark</Label>
          <Input value={form.landmark} onChange={(e) => setForm((p) => ({ ...p, landmark: e.target.value }))} placeholder="(optional)" data-testid={`${testIdPrefix}-landmark`} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">City *</Label>
            <Input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} data-testid={`${testIdPrefix}-city`} />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">State *</Label>
            <Input value={form.state} onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))} data-testid={`${testIdPrefix}-state`} />
          </div>
        </div>
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">PIN code</Label>
          <Input value={form.pincode} onChange={(e) => setForm((p) => ({ ...p, pincode: e.target.value }))} maxLength={6} inputMode="numeric" data-testid={`${testIdPrefix}-pincode`} />
        </div>
      </div>
    </div>
  );
}
